import { ModalContent, ModalCloseButton, ModalFooter, Heading, Text } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikTextArea } from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import { useBulkDeleteRequest } from 'hooks/timeOff/useBulkDeleteRequests';

import { initialWithdrawFormData } from './schema';

interface RequestWithdrawConfirmationModalProps {
  onModalClose: () => void;
  isModelOpen: boolean;
  dialogIds: { ids?: number[] };
  type?: 'delete' | 'withdraw';
  handleRequestWithdrawSuccess: () => void;
}

const RequestWithdrawConfirmationModal: React.FC<RequestWithdrawConfirmationModalProps> = ({
  isModelOpen,
  dialogIds,
  type,
  onModalClose,
  handleRequestWithdrawSuccess,
}) => {
  const { t } = useTranslation(['timeOff', 'common']);

  const { status: deleteRequestStatus, mutateAsync: mutateAsyncDelete } = useBulkDeleteRequest();

  const handleRequest = async (values: typeof initialWithdrawFormData) => {
    const postData = {
      ids: dialogIds?.ids || [],
      reason: values.reason,
    };

    mutateAsyncDelete(postData).then(() => {
      handleRequestWithdrawSuccess();
    });
  };

  return (
    <ControllableModal onClose={onModalClose} isOpen={isModelOpen} isCentered>
      <ModalContent p="6" pt="10" textAlign="center">
        <ModalCloseButton />
        <Heading as="h6" size="md" mb="2">
          {type === 'withdraw' ? t('timeOff:withdrawRequestConfirmation') : t('timeOff:deleteConfirmation')}
        </Heading>
        <Text color="gray.600" mb={6}>
          {type === 'withdraw'
            ? t('timeOff:withdrawRequestConfirmationModalSubtitle')
            : t('timeOff:deleteConfirmationModalSubtitle')}
        </Text>

        <Formik initialValues={initialWithdrawFormData} enableReinitialize onSubmit={handleRequest}>
          {() => (
            <Form noValidate>
              <FormikTextArea name="reason" label={`Reason for ${type === 'withdraw' ? 'Withdrawal' : 'Deletion'}`} />
              <ModalFooter>
                <Button variant="ghost" onClick={onModalClose}>
                  {t('common:cancel')}
                </Button>
                <Button
                  fontWeight="semibold"
                  sx={{ bg: 'rgba(255, 83, 83, 0.1)', color: 'secondary.500' }}
                  _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  _active={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                  ml="5"
                  isLoading={deleteRequestStatus === 'loading'}
                  type="submit"
                >
                  {type === 'withdraw' ? t('common:withdraw') : t('common:delete')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </ControllableModal>
  );
};

export default RequestWithdrawConfirmationModal;
